import React, { useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import convertToBase64 from "../utils/convertToBase64";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../components/utils";
import { useUploadGrantLetterTemplateFiles } from "../queries/siteSettings";
import { validateDocumentTemplate } from "../api/Esop";

type UploadResult = {
  created: { id: string; templateName: string; s3Path: string }[];
  errors: { fileName: string; reason: string }[];
};

interface BulkUploadTemplatesDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  onSuccess?: (response: UploadResult) => void;
  /** Rendered between the header and the dropzone (e.g. a "N grants selected" badge) */
  headerBadge?: React.ReactNode;
  /** Rendered inside the body section, after the hidden file input (e.g. an Instructions list) */
  bodyExtras?: React.ReactNode;
  /** List of existing template names to block duplicate uploads */
  existingFileNames?: string[];
}

const ACCEPTED_EXTENSIONS = [".docx", ".zip"];
const ACCEPTED_MIME = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/zip",
  "application/x-zip-compressed",
];

function isAcceptedFile(file: File): boolean {
  const rawExt = file.name.split(".").pop()?.toLowerCase() ?? "";
  const ext = `.${rawExt}`;
  return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_MIME.includes(file.type);
}

export default function BulkUploadTemplatesDialog({
  open,
  onClose,
  title = "Bulk Upload Templates",
  onSuccess,
  headerBadge,
  bodyExtras,
  existingFileNames = [],
}: BulkUploadTemplatesDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [validationResults, setValidationResults] = useState<
    { name: string; valid: boolean; reason: string | null }[]
  >([]);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validating, setValidating] = useState(false);

  const { mutate: uploadTemplateFiles, isLoading: uploading } =
    useUploadGrantLetterTemplateFiles();

  function handleClose() {
    setSelectedFiles([]);
    setUploadResult(null);
    setValidationResults([]);
    onClose();
  }

  function addFiles(incoming: FileList | File[]) {
    const valid: File[] = [];
    const invalid: string[] = [];
    const duplicates: string[] = [];
    const existingNamesLower = new Set(
      existingFileNames.map((n) => n.replace(/\.docx$/i, "").toLowerCase())
    );
    Array.from(incoming).forEach((f) => {
      if (!isAcceptedFile(f)) {
        invalid.push(f.name);
      } else {
        const baseName = f.name.replace(/\.(docx|zip)$/i, "").toLowerCase();
        if (existingNamesLower.has(baseName)) {
          duplicates.push(f.name);
        } else {
          valid.push(f);
        }
      }
    });
    if (invalid.length > 0) {
      toast.error(
        `Unsupported file type: ${invalid.join(
          ", "
        )}. Only .docx and .zip are allowed.`
      );
    }
    if (duplicates.length > 0) {
      toast.error(
        `File${
          duplicates.length !== 1 ? "s" : ""
        } already exist and cannot be uploaded: ${duplicates.join(", ")}`
      );
    }
    if (valid.length > 0) {
      setSelectedFiles((prev) => {
        const selectedNames = new Set(prev.map((f) => f.name));
        const newFiles = valid.filter((f) => !selectedNames.has(f.name));
        return [...prev, ...newFiles];
      });
    }
  }

  function removeFile(index: number) {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }

  async function handleUpload() {
    const docxFiles = selectedFiles.filter(
      (file) =>
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    );

    if (docxFiles.length > 0) {
      setValidating(true);
      try {
        const results = await Promise.all(
          docxFiles.map(async (file) => {
            const result = await validateDocumentTemplate(file, "GRANT_LETTER")
              .then((res) => res)
              .catch((err) => {
                const errMsg = err.response?.data?.errorRaw;
                return {
                  data: {
                    valid: false,
                    errorRaw: errMsg || "Validation error",
                  },
                };
              });
            return {
              valid: result?.data?.valid ?? false,
              name: file.name,
              reason: result?.data?.errorRaw || "Validation failed",
            };
          })
        );
        setValidationResults(results);
        if (results.some((res) => !res.valid)) {
          setShowValidationDialog(true);
          toast.error(
            "Please resolve the validation errors before uploading the files."
          );
          return;
        }
      } finally {
        setValidating(false);
      }
    }
    const filePayloads = await Promise.all(
      selectedFiles.map(async (file) => ({
        name: file.name,
        mimetype: file.type || "application/octet-stream",
        data: (await convertToBase64(file)) as string,
      }))
    );
    uploadTemplateFiles(
      { files: filePayloads },
      {
        onSuccess: (response) => {
          setUploadResult(response);
          setSelectedFiles([]);
          if (
            response.created?.length > 0 &&
            (!response.errors || response.errors.length === 0)
          ) {
            toast.success(
              `${response.created.length} file${
                response.created.length !== 1 ? "s" : ""
              } uploaded successfully`
            );
          } else if (
            response.created?.length > 0 &&
            response.errors?.length > 0
          ) {
            toast.warning(
              `${response.created.length} file${
                response.created.length !== 1 ? "s" : ""
              } uploaded, ${response.errors.length} failed`
            );
          } else if (response.errors?.length > 0) {
            toast.error(
              `Upload failed for ${response.errors.length} file${
                response.errors.length !== 1 ? "s" : ""
              }`
            );
          }
          onSuccess?.(response);
        },
        onError: (error: any) => {
          const msg =
            error?.response?.data?.errorMessage ??
            "Upload failed. Please try again.";
          toast.error(msg);
        },
      }
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md">
      <div className="w-[800px] max-w-[calc(100vw-2rem)] mx-auto bg-white rounded-lg max-h-[85vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="justify-between px-10 text-lg font-medium border-b py-7">
          <h6 className="flex justify-between">
            {title}
            <span onClick={handleClose} className="cursor-pointer">
              X
            </span>
          </h6>
        </div>

        {headerBadge && <div className="px-6 pt-4">{headerBadge}</div>}

        <VStack className="p-6 gap-5">
          {/* Dropzone */}
          <div
            onClick={() => !uploadResult && fileInputRef.current?.click()}
            onDragOver={!uploadResult ? handleDragOver : undefined}
            onDragLeave={!uploadResult ? handleDragLeave : undefined}
            onDrop={!uploadResult ? handleDrop : undefined}
            className={`flex flex-col items-center justify-center p-10 min-h-[180px] border-2 border-dashed rounded-lg transition-colors duration-200 ${
              uploadResult
                ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                : isDragActive
                ? "border-blue-500 bg-blue-50 cursor-pointer"
                : "border-gray-300 bg-gray-50 hover:bg-gray-100 cursor-pointer"
            }`}
          >
            <Icon
              icon="material-symbols:cloud-upload-outline"
              className="text-gray-400 mb-3"
              width={48}
              height={48}
            />
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-semibold text-blue-600 hover:underline">
                Click to upload
              </span>{" "}
              or drag and drop
            </p>
            <p className="text-xs text-gray-400">
              .docx or .zip files (single, multiple, or zipped)
            </p>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".docx,.zip"
            multiple
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />

          {bodyExtras}

          {/* Selected files list */}
          {selectedFiles.length > 0 && (
            <VStack className="gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Selected Files ({selectedFiles.length})
              </span>
              <VStack className="gap-1 max-h-[200px] overflow-auto">
                {selectedFiles.map((file, idx) => (
                  <HStack
                    key={`${file.name}-${idx}`}
                    className="items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="text-sm text-gray-700 truncate flex-1">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                    >
                      <Icon icon="mdi:close-circle" width={16} />
                    </button>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          )}
        </VStack>

        {/* Upload Results Summary */}
        {uploadResult && (
          <VStack className="px-6 gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Upload Summary
            </span>
            <VStack className="gap-1 max-h-[200px] overflow-auto">
              {uploadResult.created.map((t) => (
                <HStack
                  key={t.id}
                  className="items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200"
                >
                  <Icon
                    icon="mdi:check-circle"
                    className="text-green-500 shrink-0"
                    width={16}
                  />
                  <span className="text-sm text-gray-700 truncate flex-1">
                    {t.templateName}
                  </span>
                  <span className="text-xs text-green-600 shrink-0">
                    Uploaded
                  </span>
                </HStack>
              ))}
              {uploadResult.errors.map((e, i) => (
                <HStack
                  key={i}
                  className="items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200"
                >
                  <Icon
                    icon="mdi:close-circle"
                    className="text-red-500 shrink-0"
                    width={16}
                  />
                  <span className="text-sm text-gray-700 truncate flex-1">
                    {e.fileName}
                  </span>
                  <span
                    className="text-xs text-red-500 shrink-0 max-w-[180px] text-right truncate"
                    title={e.reason}
                  >
                    {e.reason}
                  </span>
                </HStack>
              ))}
            </VStack>
          </VStack>
        )}

        {/* Footer */}
        <HStack className="mt-auto justify-between px-6 py-4 border-t border-gray-100 bg-white">
          <ButtonPrimary1
            onClick={() => {
              setSelectedFiles([]);
              setUploadResult(null);
            }}
            className="text-gray-600"
          >
            Clear
          </ButtonPrimary1>
          {uploadResult ? (
            <ButtonPrimary onClick={handleClose}>Done</ButtonPrimary>
          ) : (
            <ButtonPrimary
              onClick={handleUpload}
              loading={uploading || validating}
              disabled={selectedFiles.length === 0 || uploading || validating}
            >
              Upload
            </ButtonPrimary>
          )}
        </HStack>
      </div>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
      >
        <div className="bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-lg font-semibold text-black-501">
              File Validation Results
            </p>
            <span
              className="cursor-pointer text-gray-400 hover:text-gray-600"
              onClick={() => setShowValidationDialog(false)}
            >
              <Icon icon="mdi:close" width={20} height={20} />
            </span>
          </div>
          <VStack className="gap-2">
            {validationResults.map((result, index) => (
              <HStack
                key={index}
                className={`items-start gap-3 p-3 rounded-md border ${
                  result.valid
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <Icon
                  icon={result.valid ? "mdi:check-circle" : "mdi:close-circle"}
                  width={20}
                  height={20}
                  className={
                    result.valid
                      ? "text-green-500 mt-0.5 shrink-0"
                      : "text-red-500 mt-0.5 shrink-0"
                  }
                />
                <VStack className="gap-0.5">
                  <p className="text-sm font-medium text-gray-800">
                    {result.name}
                  </p>
                  {!result.valid && (
                    <p className="text-xs text-red-600">{result.reason}</p>
                  )}
                  {result.valid && (
                    <p className="text-xs text-green-600">Valid template</p>
                  )}
                </VStack>
              </HStack>
            ))}
          </VStack>
          <div className="flex justify-end mt-5">
            <ButtonPrimary onClick={() => setShowValidationDialog(false)}>
              Close
            </ButtonPrimary>
          </div>
        </div>
      </Dialog>
    </Dialog>
  );
}
